import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { MediaProvider } from "@/components/media/context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Lock, 
  User, 
  Home as HomeIcon, 
  Image, 
  FileText, 
  Settings, 
  Users as UsersIcon,
  ChevronRight
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import MediaControlPanel from "@/components/media/MediaControlPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ContentEditor from "@/components/admin/ContentEditor";

const ControlPanelPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Simple hardcoded authentication
  // In a real app, this would use a secure authentication system
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      // Check credentials (hardcoded for demo)
      if (username === "admin" && password === "password") {
        setIsAuthenticated(true);
        toast({
          title: language === "en" ? "Login successful" : "تم تسجيل الدخول بنجاح",
          description: language === "en" ? "Welcome to the control panel" : "مرحبًا بك في لوحة التحكم",
        });
      } else {
        toast({
          variant: "destructive",
          title: language === "en" ? "Login failed" : "فشل تسجيل الدخول",
          description: language === "en" ? "Invalid username or password" : "اسم المستخدم أو كلمة المرور غير صالحة",
        });
      }
      setIsLoading(false);
    }, 1000);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername("");
    setPassword("");
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Header */}
      <header className="pt-32 pb-12 bg-palsustain-beige/30">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-palsustain-green mb-4">
            {language === "en" ? "Control Panel" : "لوحة التحكم"}
          </h1>
          <p className="text-lg md:text-xl text-palsustain-charcoal/80 max-w-3xl mx-auto">
            {language === "en" 
              ? "Manage website content and media" 
              : "إدارة محتوى الموقع والوسائط"
            }
          </p>
        </div>
      </header>
      
      <div className="container mx-auto px-4 py-8">
        {!isAuthenticated ? (
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-center">
                  {language === "en" ? "Login Required" : "تسجيل الدخول مطلوب"}
                </CardTitle>
                <CardDescription className="text-center">
                  {language === "en" 
                    ? "Please enter your credentials to access the control panel" 
                    : "يرجى إدخال بيانات الاعتماد للوصول إلى لوحة التحكم"
                  }
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleLogin}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={language === "en" ? "Username" : "اسم المستخدم"}
                        className="pl-10"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="password"
                        placeholder={language === "en" ? "Password" : "كلمة المرور"}
                        className="pl-10"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    type="submit" 
                    className="w-full bg-palsustain-green hover:bg-palsustain-green/90"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      language === "en" ? "Logging in..." : "جاري تسجيل الدخول..."
                    ) : (
                      language === "en" ? "Login" : "تسجيل الدخول"
                    )}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </div>
        ) : (
          <div className="mb-10">
            {/* Admin Panel Header */}
            <div className="mb-6 bg-palsustain-green/10 p-4 rounded-lg border border-palsustain-green/20">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-palsustain-green">
                  {language === "en" ? "Admin Dashboard" : "لوحة تحكم المسؤول"}
                </h2>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="border-palsustain-green text-palsustain-green hover:bg-palsustain-green/10"
                >
                  {language === "en" ? "Logout" : "تسجيل الخروج"}
                </Button>
              </div>
            </div>
            
            <Tabs defaultValue="home-content" className="w-full">
              <TabsList className="mb-8 w-full justify-start bg-palsustain-cream border border-palsustain-green/20 overflow-x-auto">
                <TabsTrigger value="home-content" className="data-[state=active]:bg-palsustain-green data-[state=active]:text-white">
                  <HomeIcon className="w-4 h-4 mr-2" />
                  {language === "en" ? "Home Page" : "الصفحة الرئيسية"}
                </TabsTrigger>
                <TabsTrigger value="media" className="data-[state=active]:bg-palsustain-green data-[state=active]:text-white">
                  <Image className="w-4 h-4 mr-2" />
                  {language === "en" ? "Media" : "الوسائط"}
                </TabsTrigger>
                <TabsTrigger value="users" className="data-[state=active]:bg-palsustain-green data-[state=active]:text-white">
                  <UsersIcon className="w-4 h-4 mr-2" />
                  {language === "en" ? "Users" : "المستخدمين"}
                </TabsTrigger>
                <TabsTrigger value="settings" className="data-[state=active]:bg-palsustain-green data-[state=active]:text-white">
                  <Settings className="w-4 h-4 mr-2" />
                  {language === "en" ? "Settings" : "الإعدادات"}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="home-content">
                <ContentEditor />
              </TabsContent>
              
              <TabsContent value="media">
                <MediaProvider>
                  <MediaControlPanel forceEditMode={true} />
                </MediaProvider>
              </TabsContent>
              
              <TabsContent value="users">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {language === "en" ? "User Management" : "إدارة المستخدمين"}
                    </CardTitle>
                    <CardDescription>
                      {language === "en" 
                        ? "Manage website users and access permissions." 
                        : "إدارة مستخدمي الموقع وأذونات الوصول."
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      {language === "en" 
                        ? "User management functionality will be implemented in a future update." 
                        : "سيتم تنفيذ وظيفة إدارة المستخدمين في تحديث مستقبلي."
                      }
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="settings">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {language === "en" ? "Website Settings" : "إعدادات الموقع"}
                    </CardTitle>
                    <CardDescription>
                      {language === "en" 
                        ? "Configure global website settings." 
                        : "تكوين إعدادات الموقع العالمية."
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      {language === "en" 
                        ? "Settings functionality will be implemented in a future update." 
                        : "سيتم تنفيذ وظيفة الإعدادات في تحديث مستقبلي."
                      }
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default ControlPanelPage;
